import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Store as StoreIcon } from '@mui/icons-material';
import { ChatCircleText, Heart, Plus, ArrowLeft, MagnifyingGlass, Image as ImageIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { supplierCategoryLabels, supplierCategoryValues } from '../../config/supplierTaxonomy';
import useThemeStyles from '../../theme/useThemeStyles';

const getAddressPreview = (address, addressType) => {
  if (!address || addressType === 'private') {
    if (address && addressType === 'private') {
      const parts = address.split(',').map(part => part.trim());
      if (parts.length >= 2) return `Based in ${parts[parts.length - 2]}`;
    }
    return 'Location not specified';
  }
  const parts = address.split(',').map(part => part.trim());
  return parts.length >= 2 ? parts.slice(-2).join(', ') : address;
};

const getCategoryLabel = (category) =>
  (category && supplierCategoryLabels[category]) || (category ? category.charAt(0).toUpperCase() + category.slice(1) : '');

function FindSupplierCard() {
  const navigate = useNavigate();
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Box
        component="div"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && navigate('/supplier-search')}
        sx={(theme) => ({
          width: '100%',
          height: '100%',
          minHeight: 260,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.2)',
          border: '2px dashed',
          borderColor: 'grey.400',
          boxShadow: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s',
          p: 3,
          color: 'grey.600',
          font: 'inherit',
          textAlign: 'center',
          '&:hover': {
            bgcolor: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            boxShadow: '0 6px 12px rgba(0,0,0,0.08)',
            color: 'white',
          },
        })}
        onClick={() => navigate('/supplier-search')}
      >
        <MagnifyingGlass size={32} weight="regular" />
        <Typography variant="body2" fontWeight={600} color="inherit" sx={{ mt: 1 }}>
          Find Supplier
        </Typography>
      </Box>
    </Grid>
  );
}

function SupplierCard({ userSupplier, variant, onAddToSuppliers, onRemoveToShortlist, onRemoveFromShortlist, onMessage }) {
  const navigate = useNavigate();
  const supplier = userSupplier?.supplier;
  if (!supplier?._id) return null;

  const isMySupplier = variant === 'my-suppliers';
  const locationText = getAddressPreview(supplier.address, supplier.addressType);
  const typeText = getCategoryLabel(supplier.category);

  const coverImageSrc = supplier.coverImage;

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: '0 6px 12px rgba(0,0,0,0.08)',
          },
        }}
        onClick={() => navigate(`/dashboard/supplier/${supplier._id}`)}
      >
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              height: 140,
              bgcolor: 'grey.300',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {coverImageSrc ? (
              <Box
                component="img"
                src={coverImageSrc}
                alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Box component="span" sx={{ color: 'grey.500', display: 'flex' }}>
                <ImageIcon size={40} weight="regular" />
              </Box>
            )}
          </Box>
          {!isMySupplier && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: 'error.main', '&:hover': { bgcolor: 'white' } }}
                onClick={() => onRemoveFromShortlist(userSupplier)}
                aria-label="Remove from shortlist"
              >
                <Heart size={20} weight="fill" />
              </IconButton>
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              bottom: -20,
              left: 16,
              width: 48,
              height: 48,
              borderRadius: '50%',
              border: '2px solid white',
              boxShadow: 1,
              bgcolor: 'white',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {supplier.imageUrl ? (
              <Box
                component="img"
                src={supplier.imageUrl}
                alt=""
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
              />
            ) : (
              <StoreIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            )}
          </Box>
        </Box>
        <CardContent sx={{ pt: 3, pb: 2, px: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {supplier.businessName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {locationText} · {typeText}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }} onClick={(e) => e.stopPropagation()}>
            {isMySupplier ? (
              <Button
                variant="outlined"
                startIcon={<ArrowLeft size={20} />}
                onClick={() => onRemoveToShortlist(userSupplier)}
              >
                Remove to shortlist
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<Plus size={20} weight="bold" />}
                onClick={() => onAddToSuppliers(userSupplier)}
              >
                Add to my suppliers
              </Button>
            )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onMessage(supplier._id);
              }}
              aria-label="Message"
              sx={{ ml: 'auto' }}
            >
              <ChatCircleText size={24} weight="regular" />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

function CoupleSuppliersPage() {
  const styles = useThemeStyles();
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [supplierToMove, setSupplierToMove] = useState(null);
  const [moveAction, setMoveAction] = useState(null); // 'add' | 'remove'
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [newSupplier, setNewSupplier] = useState({
    businessName: '',
    category: '',
    contactName: '',
    phone: '',
    email: '',
  });
  const navigate = useNavigate();
  const categories = supplierCategoryValues;

  const mySuppliers = allSuppliers.filter((s) => s.status === 'active' && s.supplier?._id);
  const shortlisted = allSuppliers.filter((s) => s.status === 'shortlisted' && s.supplier?._id);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/user-suppliers');
      setAllSuppliers(response.data || []);
    } catch (err) {
      setError('Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleAddToSuppliers = (userSupplier) => {
    setSupplierToMove(userSupplier);
    setMoveAction('add');
    setConfirmDialogOpen(true);
  };

  const handleRemoveToShortlist = (userSupplier) => {
    setSupplierToMove(userSupplier);
    setMoveAction('remove');
    setConfirmDialogOpen(false);
    performMove(userSupplier._id, 'shortlisted');
  };

  const handleRemoveFromShortlist = async (userSupplier) => {
    try {
      setError('');
      await api.put(`/api/user-suppliers/${userSupplier._id}`, { status: 'removed' });
      setSuccess('Removed from shortlist');
      fetchSuppliers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove from shortlist');
    }
  };

  const performMove = async (userSupplierId, newStatus) => {
    try {
      setError('');
      await api.put(`/api/user-suppliers/${userSupplierId}`, { status: newStatus });
      setSuccess(newStatus === 'active' ? 'Added to Your Suppliers' : 'Moved to shortlist');
      setConfirmDialogOpen(false);
      setSupplierToMove(null);
      setMoveAction(null);
      fetchSuppliers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update');
    }
  };

  const handleConfirmMove = () => {
    if (supplierToMove && moveAction === 'add') {
      performMove(supplierToMove._id, 'active');
    }
  };

  const handleChatWithSupplier = (supplierId) => {
    navigate(`/chat?supplier=${supplierId}`);
  };

  const handleAddManualSupplier = async () => {
    try {
      setError('');
      await api.post('/api/user-suppliers/manual', newSupplier);
      setSuccess('Supplier added successfully');
      setManualDialogOpen(false);
      setNewSupplier({ businessName: '', category: '', contactName: '', phone: '', email: '' });
      fetchSuppliers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add supplier');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Page header: title + Find supplier */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          My Wedding Suppliers
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={20} weight="bold" />}
          onClick={() => setManualDialogOpen(true)}
          sx={{ bgcolor: '#ef4192', '&:hover': { bgcolor: '#d63880' } }}
        >
          Add Unlisted Supplier
        </Button>
      </Box>

      {/* Your Suppliers */}
      <Typography variant="h5" component="h2" fontWeight={600} sx={{ mb: 2 }}>
        Your Suppliers
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {mySuppliers.map((us) => (
          <SupplierCard
            key={us._id}
            userSupplier={us}
            variant="my-suppliers"
            onAddToSuppliers={handleAddToSuppliers}
            onRemoveToShortlist={handleRemoveToShortlist}
            onRemoveFromShortlist={handleRemoveFromShortlist}
            onMessage={handleChatWithSupplier}
          />
        ))}
      </Grid>
      {mySuppliers.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You haven&apos;t added any suppliers yet. Add from your shortlist or find new suppliers.
        </Typography>
      )}

      {/* Shortlisted Suppliers */}
      <Typography variant="h5" component="h2" fontWeight={600} sx={{ mb: 2 }}>
        Shortlisted Suppliers
      </Typography>
      <Grid container spacing={2}>
        <FindSupplierCard />
        {shortlisted.map((us) => (
          <SupplierCard
            key={us._id}
            userSupplier={us}
            variant="shortlist"
            onAddToSuppliers={handleAddToSuppliers}
            onRemoveToShortlist={handleRemoveToShortlist}
            onRemoveFromShortlist={handleRemoveFromShortlist}
            onMessage={handleChatWithSupplier}
          />
        ))}
      </Grid>
      {shortlisted.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary">
          You haven&apos;t shortlisted any suppliers yet.
        </Typography>
      )}

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Add to Your Suppliers</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Moving this supplier from shortlist to Your Suppliers will make your wedding information
            visible to them. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmMove} variant="contained" color="primary">
            Add to Your Suppliers
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manualDialogOpen} onClose={() => setManualDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Manual Supplier</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Business Name"
              fullWidth
              value={newSupplier.businessName}
              onChange={(e) => setNewSupplier({ ...newSupplier, businessName: e.target.value })}
              required
            />
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={newSupplier.category}
                label="Category"
                onChange={(e) => setNewSupplier({ ...newSupplier, category: e.target.value })}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {supplierCategoryLabels[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Contact Name"
              fullWidth
              value={newSupplier.contactName}
              onChange={(e) => setNewSupplier({ ...newSupplier, contactName: e.target.value })}
              required
            />
            <TextField
              label="Phone"
              fullWidth
              value={newSupplier.phone}
              onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
            />
            <TextField
              label="Email"
              fullWidth
              type="email"
              value={newSupplier.email}
              onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddManualSupplier}
            variant="contained"
            disabled={!newSupplier.businessName || !newSupplier.category || !newSupplier.contactName}
          >
            Add Supplier
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CoupleSuppliersPage;
